import {useState} from 'react';
import {DateTime} from 'luxon';
import {useQuery} from 'react-query';
import {Link, useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useLang} from '../../../_metronic/i18n/Metronici18n';
import {useNativeAuth} from '../../modules/auth/native/NativeAuthProvider';
import {PageTitle} from '../../../_metronic/layout/core';
import {getLastBills} from './api';
import {getOrdersByClient} from '../order-tracking/api';
import {getOpenTickets, getArchivedTicket} from '../../../Domains/Ticket/Api/tickets';
import {getServicesByClient} from '../../../Domains/Service/Api/api';
import {useGetDatabase} from '../../../Domains/Service/Hooks/DatabaseManager';
import {useCompteFTPByBuId} from '../../../Domains/Service/Hooks/HebergementManager';
import {ListService, DBService, compteFTP} from '../../../Domains/Service/Types/service';
import {BillCategory, BillSearchResponse} from '../../interfaces/bill';
import {ListOrder} from '../../interfaces/order';
import {TicketFromApi} from '../../interfaces/ticket';
import {
  getStatusColor,
  getStatusTextColor,
  getStatusLabel as getTicketStatusLabel,
} from '../../../Domains/Ticket/Helpers/stateHelper';
import {getOrderStatusColor, getOrderStatusLabel} from '../order-tracking/orders/orderStatusHelper';
import EtatBadge from '../../../Domains/Service/Components/EtatBadge';
import {ticketDetailPath} from '../../../Domains/Ticket/Helpers/ticketNavigation';
import statusBadgeStyles from '../../../Domains/Ticket/Components/TicketStatusBadge.module.css';
import {useGetMe} from '../../hooks/useGetMe';
import {
  useImpersonateFirstName,
  usePermissions,
} from '../../modules/auth/authorization/PermissionProvider';
import {checkIfAuthorized} from '../../modules/auth/authorization/api';
import {formatAmount} from '../../common/tools/amount';
import {formatDate} from '../../common/tools/date';
import SearchModal from './SearchModal';
import DownloadBill from './last-bills/DownloadBill';
import LatestNews from './news/LatestNews';
import CurrentlyCompact from './currently/CurrentlyCompact';
import SecuredComponent from '../../modules/auth/authorization/SecuredComponent';
import styles from './DashboardV2.module.css';

const TICKETS_LIMIT = 2;
const BILLS_LIMIT = 4;
const ORDERS_LIMIT = 4;
const SERVICES_LIMIT = 3;

const billStatus = (
  bill: BillSearchResponse,
  paidLabel: string,
  unpaidLabel: string
): {label: string; color: string} => {
  if (bill.isInvoiceOpen === null) {
    return {label: '—', color: '#ababab'};
  }
  return bill.isInvoiceOpen
    ? {label: unpaidLabel, color: '#de8100'}
    : {label: paidLabel, color: '#5da170'};
};

const parseTicketDate = (dateStr: string): DateTime => {
  const iso = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  return DateTime.fromISO(iso);
};

const formatTicketOpening = (dateStr: string, locale: string, format: string): string => {
  const dt = parseTicketDate(dateStr);
  return dt.isValid ? dt.setLocale(locale).toFormat(format) : dateStr;
};

const formatTicketUpdate = (dateStr: string, locale: string): string | null => {
  const dt = parseTicketDate(dateStr);
  return dt.isValid ? dt.setLocale(locale).toRelative({base: DateTime.now()}) : null;
};

const normalizeState = (state: string | null): string => (state ?? '').trim().toLowerCase();

const ONGOING_STATES = new Set([
  '',
  'nouveau',
  'pris en charge',
  'en attente',
  'attente de fermeture auto',
  'en escalade',
  'en cours',
  'gelé',
]);

const RESOLVED_STATES = new Set(['fermé avec succés', 'fermé sans succés', 'rétabli']);

const isOngoingTicket = (t: TicketFromApi): boolean => ONGOING_STATES.has(normalizeState(t.state));
const isResolvedTicket = (t: TicketFromApi): boolean =>
  RESOLVED_STATES.has(normalizeState(t.state));

const isActiveService = (s: ListService): boolean =>
  !(s.state ?? '').toLowerCase().includes('résili');

const serviceLocation = (s: ListService): string | null =>
  s.site?.city || s.buName || s.link_name || null;

interface MiniCardItem {
  id: string;
  label: string;
  sublabel?: string | null;
  active: boolean;
}

const serviceToItem = (s: ListService): MiniCardItem => ({
  id: `svc-${s.serviceId}`,
  label: s.serviceName,
  sublabel: serviceLocation(s),
  active: isActiveService(s),
});

const dbToItem = (db: DBService, sublabel: string): MiniCardItem => ({
  id: `db-${db.id}`,
  label: db.name,
  sublabel,
  active: db.active === 1,
});

const ftpToItem = (ftp: compteFTP, sublabel: string): MiniCardItem => ({
  id: `ftp-${ftp.id}`,
  label: ftp.uid,
  sublabel,
  active: ftp.active === 1,
});

interface ServiceMiniCardProps {
  title: string;
  icon: string;
  iconColor: string;
  items: MiniCardItem[] | undefined;
  isLoading: boolean;
  manageLabel: string;
  manageTo: string;
  emptyMessage: string;
  activeCountLabel: string;
}

const ServiceMiniCard = ({
  title,
  icon,
  iconColor,
  items,
  isLoading,
  manageLabel,
  manageTo,
  emptyMessage,
  activeCountLabel,
}: ServiceMiniCardProps): JSX.Element => {
  const active = (items ?? []).filter((i) => i.active);
  const visible = active.slice(0, SERVICES_LIMIT);
  return (
    <div className="card h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-center mb-5">
          <span
            className="d-flex align-items-center justify-content-center rounded me-3 flex-shrink-0"
            style={{width: 44, height: 44, backgroundColor: `${iconColor}1a`}}
          >
            <i className={`bi ${icon} fs-3`} style={{color: iconColor}} />
          </span>
          <div>
            <div className="fw-bold fs-5 text-gray-900">{title}</div>
            <div className="text-muted fs-7">{activeCountLabel}</div>
          </div>
        </div>

        <div className="flex-grow-1">
          {isLoading && <Spinner />}
          {!isLoading && visible.length === 0 && <EmptyRow message={emptyMessage} />}
          {visible.map((item) => (
            <div key={item.id} className="d-flex align-items-center justify-content-between py-2">
              <span className="text-gray-800 text-truncate">
                {item.label}
                {item.sublabel && <span className="text-muted"> — {item.sublabel}</span>}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-4 border-top">
          <Link to={manageTo} className={`text-primary fw-semibold ${styles.softLink}`}>
            {manageLabel} →
          </Link>
        </div>
      </div>
    </div>
  );
};

const Spinner = (): JSX.Element => (
  <div className="text-center py-6">
    <span className="spinner-border spinner-border-sm text-primary" role="status" />
  </div>
);

const EmptyRow = ({message}: {message: string}): JSX.Element => (
  <div className="text-center text-muted py-6">{message}</div>
);

const DashboardV2 = (): JSX.Element => {
  const navigate = useNavigate();
  const intl = useIntl();
  const lang = useLang();
  const {claims} = useNativeAuth();
  const customer = useGetMe();
  const impersonateFirstName = useImpersonateFirstName();
  const userPermissions = usePermissions();
  const can = (permissions: string[]): boolean => checkIfAuthorized(userPermissions, permissions);
  // Mirror the backend route middlewares so we never call an endpoint that would 403.
  const canSeeTickets = can(['voirTicketsEnCours']); // api/reclamation/*
  const canSeeBills = can(['voirTableauDeBord', 'voirDernieresFactures']); // api/billing/bills/lasts
  const canSeeOrders = can(['voirSuiviCommande']); // api/orders
  const canSeeServices = can(['voirEtatParc']); // api/services, api/services/hebergement/*
  // Frontend route guards (routesConfig.tsx) for the "see all" links.
  const canOpenBillsPage = can(['voirDocuments', 'voirDocumentsRelationClient']);
  const canOpenOrdersList = can(['voirSuiviCommande', 'voirCommandesEnCoursResilie']);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // Name comes from the JWT claims.
  // When impersonating a user, the greeting shows that user's first name instead.
  const displayName = claims?.name as string;
  const prenom = impersonateFirstName || (displayName ?? '').split(' ')[1] || '';
  const dateDuJour = DateTime.now()
    .setLocale(lang)
    .toFormat(intl.formatMessage({id: 'DASHBOARD.DATE_FORMAT'}));
  const ticketDateFormat = intl.formatMessage({id: 'DASHBOARD.TICKETS.DATE_FORMAT'});
  const paidLabel = intl.formatMessage({id: 'DASHBOARD.BILLS.STATUS.PAID'});
  const unpaidLabel = intl.formatMessage({id: 'DASHBOARD.BILLS.STATUS.UNPAID'});
  const dbSublabel = intl.formatMessage({id: 'DASHBOARD.SERVICES.DB.SUBLABEL'});
  const ftpSublabel = intl.formatMessage({id: 'DASHBOARD.SERVICES.FTP.SUBLABEL'});

  const {data: tickets, isLoading: openTicketsLoading} = useQuery<TicketFromApi[]>(
    ['tickets', 'open'],
    getOpenTickets,
    {enabled: canSeeTickets}
  );

  const ongoingTickets = (tickets ?? []).filter(isOngoingTicket);
  const hasOngoingTickets = ongoingTickets.length > 0;

  const {data: archivedTickets, isLoading: archivedTicketsLoading} = useQuery<TicketFromApi[]>(
    ['tickets', 'closed'],
    getArchivedTicket,
    {enabled: canSeeTickets && !openTicketsLoading && !hasOngoingTickets}
  );
  const {
    data: bills,
    isLoading: billsLoading,
    isError: billsError,
  } = useQuery<BillSearchResponse[]>(['last-bills', BILLS_LIMIT], () => getLastBills(BILLS_LIMIT), {
    enabled: canSeeBills,
  });
  const {data: orders, isLoading: ordersLoading} = useQuery<ListOrder[]>(
    ['get-orders-by-client', null],
    async () => getOrdersByClient(null),
    {enabled: canSeeOrders}
  );
  const {data: services, isLoading: servicesLoading} = useQuery<ListService[]>(
    ['get-service-by-client'],
    () => getServicesByClient(),
    {enabled: canSeeServices}
  );
  const {data: domaines, isLoading: domainesLoading} = useQuery<ListService[]>(
    ['get-service-by-client-domaine'],
    () => getServicesByClient('DOMAIN'),
    {enabled: canSeeServices}
  );
  const {data: emails, isLoading: emailsLoading} = useQuery<ListService[]>(
    ['get-service-by-client-mail'],
    () => getServicesByClient('MAIL'),
    {enabled: canSeeServices}
  );
  const {data: baies, isLoading: baiesLoading} = useQuery<ListService[]>(
    ['get-service-by-client-baies'],
    () => getServicesByClient('BAY'),
    {enabled: canSeeServices}
  );
  const {data: databases, isLoading: databasesLoading} = useGetDatabase('', canSeeServices);
  const {data: comptesFtp, isLoading: comptesFtpLoading} = useCompteFTPByBuId('', canSeeServices);

  const hasType = (s: ListService, types: string[]): boolean =>
    s.type.some((t) => types.includes(t));
  const connexionsItems = (services ?? [])
    .filter((s) => hasType(s, ['Connexion']))
    .map(serviceToItem);
  const telephonieItems = (services ?? [])
    .filter((s) => hasType(s, ['TELEPHONY']))
    .map(serviceToItem);
  const domainesItems = (domaines ?? []).map(serviceToItem);
  const emailsItems = (emails ?? []).map(serviceToItem);
  const webBddItems = [
    ...(databases ?? []).map((db) => dbToItem(db, dbSublabel)),
    ...(comptesFtp ?? []).map((ftp) => ftpToItem(ftp, ftpSublabel)),
  ];
  const infrastructureItems = (baies ?? []).map(serviceToItem);

  const resolvedTickets = (archivedTickets ?? []).filter(isResolvedTicket);

  const ticketsLoading = openTicketsLoading || (!hasOngoingTickets && archivedTicketsLoading);
  const ticketsCardTitle = hasOngoingTickets
    ? intl.formatMessage({id: 'DASHBOARD.TICKETS.ONGOING_TITLE'})
    : intl.formatMessage({id: 'DASHBOARD.TICKETS.LATEST_TITLE'});
  const sourceTickets = hasOngoingTickets ? ongoingTickets : resolvedTickets;
  const ticketCount = sourceTickets.length;
  const visibleTickets = [...sourceTickets]
    .sort((a, b) => Number(b.numDossier) - Number(a.numDossier))
    .slice(0, TICKETS_LIMIT);
  const visibleBills = (bills ?? []).slice(0, BILLS_LIMIT);
  const visibleOrders = [...(orders ?? [])]
    .filter((order) => order.status !== null)
    .sort((a, b) => (b.opening ?? '').localeCompare(a.opening ?? ''))
    .slice(0, ORDERS_LIMIT);

  const serviceEmptyMsg = intl.formatMessage({id: 'DASHBOARD.SERVICES.EMPTY'});
  const serviceActiveCount = (items: MiniCardItem[] | undefined) =>
    intl.formatMessage(
      {id: 'DASHBOARD.SERVICES.ACTIVE'},
      {count: (items ?? []).filter((i) => i.active).length}
    );

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>

      {/* En-tête */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-8">
        <div>
          <h1 className="fw-bolder text-gray-900 mb-1" style={{fontSize: '1.9rem'}}>
            {intl.formatMessage({id: 'DASHBOARD.GREETING'}, {prenom})}
          </h1>
          <div className="text-muted fs-6">
            {intl.formatMessage({id: 'DASHBOARD.SUMMARY'}, {date: dateDuJour})}
          </div>
        </div>
        <div className={`position-relative mt-4 mt-md-0 ${styles.searchBox}`}>
          <i
            className="bi bi-search position-absolute text-gray-600 fs-5"
            style={{top: '50%', left: 20, transform: 'translateY(-50%)'}}
          />
          <input
            type="text"
            className={`form-control bg-white ps-13 ${styles.searchInput}`}
            placeholder={intl.formatMessage({id: 'DASHBOARD.SEARCH.PLACEHOLDER'})}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearchOpen(true);
            }}
            style={{
              paddingTop: 12,
              paddingBottom: 12,
              fontSize: '1rem',
              border: '1px solid #e6e6e6',
              borderRadius: '0.625rem',
            }}
          />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="btn btn-primary btn-sm position-absolute px-4 d-flex align-items-center justify-content-center gap-2"
            style={{top: '50%', right: 4, transform: 'translateY(-50%) scale(0.9)'}}
          >
            <i className="bi bi-search" />
            <span className={styles.searchButtonLabel}>
              {intl.formatMessage({id: 'DASHBOARD.SEARCH.BUTTON'})}
            </span>
          </button>
        </div>
      </div>

      {/* En ce moment : bannières / news */}
      <SecuredComponent requiredPermissions={['voirTableauDeBord', 'voirEnCeMoment']}>
        <div className="mb-6">
          <CurrentlyCompact />
        </div>
      </SecuredComponent>

      {/* Ligne 1 : tickets en cours + boutique en ligne */}
      <div className="row g-6 mb-6">
        {canSeeTickets && (
          <div className="col-12 col-lg-8">
            <div className="card h-100 overflow-hidden">
              <div className="card-body d-flex flex-column">
                <div className="d-flex align-items-center mb-6">
                  <span className="fs-3 fw-semibold text-celeste-primary">{ticketsCardTitle}</span>
                  <span
                    className="badge ms-2"
                    style={{backgroundColor: '#fdf3e3', color: '#f6a609', borderRadius: '50%'}}
                  >
                    {ticketCount}
                  </span>
                </div>

                {ticketsLoading && <Spinner />}
                {!ticketsLoading && visibleTickets.length === 0 && (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{minHeight: 120}}
                  >
                    <EmptyRow message={intl.formatMessage({id: 'DASHBOARD.TICKETS.EMPTY'})} />
                  </div>
                )}

                {visibleTickets.map((t) => {
                  const statusColor = getStatusColor(t.state);
                  const stateText = getTicketStatusLabel(t.state, intl);
                  const maj = t.updateAt ? formatTicketUpdate(t.updateAt, lang) : null;
                  const ticketType = (t as TicketFromApi & {type?: string}).type;
                  const typeLabel =
                    ticketType === 'technical'
                      ? intl.formatMessage({id: 'DASHBOARD.TICKETS.TYPE.TECHNICAL'})
                      : intl.formatMessage({id: 'DASHBOARD.TICKETS.TYPE.ADMINISTRATIVE'});
                  return (
                    <Link
                      key={t.numDossier}
                      to={ticketDetailPath(t, customer)}
                      className={`d-flex flex-column flex-md-row align-items-md-center border-top text-reset py-3 ${styles.clickableRow}`}
                    >
                      <div className={`d-flex flex-column flex-shrink-0 ${styles.ticketMeta}`}>
                        <span className="text-muted fs-7">
                          N° <span className="text-dark fw-semibold">{t.numDossier}</span>
                        </span>
                        <span
                          className="mt-2 d-inline-flex align-items-center text-gray-800 fs-8 px-2 py-1 rounded align-self-start"
                          style={{border: '1px solid #e4e6ef'}}
                        >
                          <i className="bi bi-chat-square-text me-2 text-gray-800" />
                          {typeLabel}
                        </span>
                      </div>

                      <div
                        className={`d-flex align-items-center justify-content-start justify-content-md-center px-md-3 flex-shrink-0 my-2 my-md-0 ${styles.ticketStatus}`}
                      >
                        <span
                          className={statusBadgeStyles.statusBadge}
                          style={{
                            backgroundColor: statusColor,
                            color: getStatusTextColor(statusColor),
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {stateText}
                        </span>
                      </div>

                      <div className="flex-grow-1 px-md-4">
                        <div className="fw-bold text-dark">{t.titre}</div>
                        <div className="d-flex flex-wrap gap-4 text-muted fs-7 mt-1">
                          <span className="d-inline-flex align-items-center">
                            <i className="bi bi-calendar me-2" />
                            {intl.formatMessage({id: 'DASHBOARD.TICKETS.OPENED_ON'})}{' '}
                            {formatTicketOpening(t.ouverture, lang, ticketDateFormat)}
                          </span>
                          {maj && (
                            <span className="d-inline-flex align-items-center">
                              <i className="bi bi-clock me-2" />
                              {intl.formatMessage({id: 'DASHBOARD.TICKETS.UPDATED'})} {maj}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}

                <div className="d-flex justify-content-between align-items-center pt-6 border-top mt-auto">
                  <Link
                    to="/support-list"
                    className={`text-primary fw-semibold ${styles.softLink}`}
                  >
                    {intl.formatMessage({id: 'DASHBOARD.TICKETS.SEE_ALL'})}
                  </Link>
                  <Link to="/support" className="btn btn-primary btn-sm">
                    <i className="bi bi-plus-lg me-2" />
                    {intl.formatMessage({id: 'DASHBOARD.TICKETS.OPEN_NEW'})}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={canSeeTickets ? 'col-12 col-lg-4' : 'col-12'}>
          <div
            className="card h-100 text-white overflow-hidden position-relative"
            style={{background: 'linear-gradient(135deg, #163a73 0%, #1b2a52 100%)'}}
          >
            <div className="card-body d-flex flex-column position-relative" style={{zIndex: 2}}>
              <div
                className="text-uppercase fw-bold fs-7 mb-2"
                style={{letterSpacing: 1, opacity: 0.7}}
              >
                {intl.formatMessage({id: 'DASHBOARD.SHOP.LABEL'})}
              </div>
              <h2 className="text-white fw-bolder mb-3 fs-2 text-nowrap">
                {intl.formatMessage({id: 'DASHBOARD.SHOP.TITLE'})}
              </h2>
              <p className="fs-6" style={{opacity: 0.85, maxWidth: 220}}>
                {intl.formatMessage({id: 'DASHBOARD.SHOP.DESC'})}
              </p>

              <div className="flex-grow-1 position-relative" style={{minHeight: 120}}>
                <div
                  className="position-absolute rounded-circle"
                  style={{
                    width: 310,
                    height: 310,
                    right: -21,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.04)',
                  }}
                />
                <div
                  className="position-absolute rounded-circle"
                  style={{
                    width: 220,
                    height: 220,
                    right: 24,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.07)',
                  }}
                />
                <svg
                  className="position-absolute"
                  style={{right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 1}}
                  width="220"
                  height="139"
                  viewBox="0 0 190 120"
                  fill="none"
                >
                  <path
                    d="M30 70 A55 55 0 0 1 60 28"
                    stroke="rgba(255,255,255,0.25)"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    d="M165 70 A55 55 0 0 0 135 28"
                    stroke="rgba(255,255,255,0.25)"
                    strokeWidth="4"
                    fill="none"
                  />
                  <line x1="70" y1="55" x2="70" y2="20" stroke="#5b9bff" strokeWidth="3" />
                  <circle cx="70" cy="17" r="4" fill="#5b9bff" />
                  <line x1="120" y1="55" x2="120" y2="20" stroke="#5b9bff" strokeWidth="3" />
                  <circle cx="120" cy="17" r="4" fill="#5b9bff" />
                  <rect x="40" y="55" width="110" height="45" rx="8" fill="#2b5bb0" />
                  <rect
                    x="40"
                    y="55"
                    width="110"
                    height="45"
                    rx="8"
                    stroke="#5b9bff"
                    strokeWidth="1.5"
                  />
                  <circle cx="62" cy="78" r="4" fill="#50cd89" />
                  <circle cx="80" cy="78" r="4" fill="#50cd89" />
                  <circle cx="98" cy="78" r="4" fill="#50cd89" />
                  <circle cx="116" cy="78" r="4" fill="#ffffff" />
                </svg>
              </div>

              <div className="mt-auto">
                <Link
                  to="/marketplace"
                  className="btn btn-light fw-bold d-inline-flex align-items-center text-nowrap"
                  style={{
                    color: '#163a73',
                    borderRadius: 10,
                    padding: '10px 16px',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) =>
                    e.currentTarget.style.setProperty('background-color', '#e4ebf2', 'important')
                  }
                  onMouseLeave={(e) => e.currentTarget.style.removeProperty('background-color')}
                >
                  <i className="bi bi-shop fs-5 me-2" style={{color: '#163a73'}} />
                  {intl.formatMessage({id: 'DASHBOARD.SHOP.CTA'})}
                  <i className="bi bi-chevron-right ms-2" style={{color: '#163a73'}} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ligne 2 : dernières factures + dernières commandes */}
      {(canSeeBills || canSeeOrders) && (
        <div className="row g-6 mb-6">
          {canSeeBills && (
            <div className={canSeeOrders ? 'col-12 col-lg-6' : 'col-12'}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-6">
                    <span className="fs-3 fw-semibold text-celeste-primary">
                      {intl.formatMessage({id: 'DASHBOARD.BILLS.TITLE'})}
                    </span>
                    {canOpenBillsPage && (
                      <Link to="/bills" className={`text-primary fw-semibold ${styles.softLink}`}>
                        {intl.formatMessage({id: 'DASHBOARD.BILLS.SEE_ALL'})}
                      </Link>
                    )}
                  </div>
                  {billsLoading && <Spinner />}
                  {!billsLoading && billsError && (
                    <div className="text-center text-muted py-6">
                      <i className="fa-solid fa-triangle-exclamation fs-2 mb-3 d-block"></i>
                      <div>{intl.formatMessage({id: 'ADMINISTRATIVE.BILL.UNAVAILABLE.LINE1'})}</div>
                      <div>{intl.formatMessage({id: 'ADMINISTRATIVE.BILL.UNAVAILABLE.LINE2'})}</div>
                    </div>
                  )}
                  {!billsLoading && !billsError && visibleBills.length === 0 && (
                    <EmptyRow message={intl.formatMessage({id: 'DASHBOARD.BILLS.EMPTY'})} />
                  )}
                  {!billsLoading && visibleBills.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-row-bordered align-middle gs-0 gy-3">
                        <thead>
                          <tr className="text-muted fw-semibold fs-7 text-uppercase">
                            <th>{intl.formatMessage({id: 'DASHBOARD.BILLS.COL.NUMBER'})}</th>
                            <th>{intl.formatMessage({id: 'DASHBOARD.BILLS.COL.ISSUED'})}</th>
                            <th>{intl.formatMessage({id: 'DASHBOARD.BILLS.COL.STATUS'})}</th>
                            <th className="text-end">
                              {intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT'})}
                            </th>
                            <th className="text-end" />
                          </tr>
                        </thead>
                        <tbody>
                          {visibleBills.map((bill) => {
                            const status = billStatus(bill, paidLabel, unpaidLabel);
                            return (
                              <tr
                                key={bill.id}
                                className={canOpenBillsPage ? styles.clickableRow : undefined}
                                onClick={() => canOpenBillsPage && navigate('/bills')}
                              >
                                <td className="fw-semibold text-gray-900">{bill.number}</td>
                                <td className="text-muted">{formatDate(bill.date)}</td>
                                <td>
                                  {bill.type === BillCategory.CREDIT ? (
                                    '-'
                                  ) : (
                                    <EtatBadge label={status.label} color={status.color} />
                                  )}
                                </td>
                                <td className="text-end fw-bold text-gray-900">
                                  {formatAmount(bill.amountIncludingTax)}
                                </td>
                                <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                  <DownloadBill bill={bill} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {canSeeOrders && (
            <div className={canSeeBills ? 'col-12 col-lg-6' : 'col-12'}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-6">
                    <span className="fs-3 fw-semibold text-celeste-primary">
                      {intl.formatMessage({id: 'DASHBOARD.ORDERS.TITLE'})}
                    </span>
                    {canOpenOrdersList && (
                      <Link to="/orders" className={`text-primary fw-semibold ${styles.softLink}`}>
                        {intl.formatMessage({id: 'DASHBOARD.ORDERS.SEE_ALL'})}
                      </Link>
                    )}
                  </div>
                  {ordersLoading && <Spinner />}
                  {!ordersLoading && visibleOrders.length === 0 && (
                    <EmptyRow message={intl.formatMessage({id: 'DASHBOARD.ORDERS.EMPTY'})} />
                  )}
                  {!ordersLoading && visibleOrders.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-row-bordered align-middle gs-0 gy-3">
                        <thead>
                          <tr className="text-muted fw-semibold fs-7 text-uppercase">
                            <th className="text-nowrap" style={{width: '1%'}}>
                              {intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.REF'})}
                            </th>
                            <th style={{maxWidth: 220}}>
                              {intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.LABEL'})}
                            </th>
                            <th className="text-nowrap">
                              {intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.STATUS'})}
                            </th>
                            <th className="text-end text-nowrap" style={{width: '1%'}}>
                              {intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.DATE'})}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleOrders.map((order) => {
                            const statusLabel = order.status ?? '';
                            return (
                              <tr
                                key={order.id}
                                className={styles.clickableRow}
                                onClick={() => order.id && navigate(`/orders/${order.id}`)}
                              >
                                <td className="fw-semibold text-gray-900 text-nowrap">
                                  {order.id}
                                </td>
                                <td className="text-muted text-truncate" style={{maxWidth: 220}}>
                                  {order.service ?? '—'}
                                </td>
                                <td className="text-nowrap">
                                  {order.status && (
                                    <EtatBadge
                                      label={getOrderStatusLabel(statusLabel, intl)}
                                      color={getOrderStatusColor(statusLabel)}
                                    />
                                  )}
                                </td>
                                <td className="text-end fw-bold text-gray-900">
                                  {formatDate(order.opening)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bannière : test d'éligibilité fibre */}
      <div className="card">
        <div className="card-body d-flex flex-column flex-md-row align-items-md-center">
          <span className="d-flex align-items-center justify-content-center me-6 mb-4 mb-md-0 flex-shrink-0">
            <svg
              width="96"
              height="96"
              viewBox="0 0 96 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Localisation"
            >
              <circle cx="48" cy="48" r="34" fill="#e8f3ff" />
              <circle cx="48" cy="48" r="24" fill="#d6e9ff" />
              <circle cx="16" cy="30" r="3" fill="#1c63c4" />
              <circle cx="84" cy="42" r="3" fill="#1c63c4" />
              <circle cx="13" cy="62" r="3" fill="#1c63c4" />
              <circle cx="78" cy="70" r="2.5" fill="#1c63c4" />
              <circle cx="9" cy="46" r="2.5" fill="#9dc3f0" />
              <circle cx="88" cy="58" r="2" fill="#9dc3f0" />
              <path
                d="M48 28c-7.18 0-13 5.82-13 13 0 9.75 13 23 13 23s13-13.25 13-23c0-7.18-5.82-13-13-13z"
                fill="#1c63c4"
              />
              <circle cx="48" cy="41" r="5" fill="#ffffff" />
            </svg>
          </span>
          <div className="flex-grow-1 me-md-6">
            <div className="text-uppercase fw-bold fs-8 text-muted mb-1" style={{letterSpacing: 1}}>
              {intl.formatMessage({id: 'DASHBOARD.ELIGIBILITY.LABEL'})}
            </div>
            <div className="fw-bold fs-4 text-gray-900">
              {intl.formatMessage({id: 'DASHBOARD.ELIGIBILITY.TITLE'})}
            </div>
            <div className="text-muted">
              {intl.formatMessage({id: 'DASHBOARD.ELIGIBILITY.DESC'})}
            </div>
          </div>
          <div className="d-flex align-items-center mt-4 mt-md-0">
            <a
              href="https://www.celeste.fr/carte-eligibilite-acces-internet-pour-entreprise/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary fw-bold d-inline-flex align-items-center text-nowrap"
              style={{borderRadius: 10, padding: '12px 20px'}}
            >
              <i className="bi bi-geo-alt fs-5 me-3" />
              {intl.formatMessage({id: 'DASHBOARD.ELIGIBILITY.CTA'})}
              <i className="bi bi-chevron-right ms-3" />
            </a>
          </div>
        </div>
      </div>

      {canSeeServices && (
        <>
          {/* Ligne 3 : aperçu des services par catégorie */}
          <div className="d-flex justify-content-between align-items-center mt-8 mb-6">
            <span className="fw-bold fs-2 text-gray-900">
              {intl.formatMessage({id: 'DASHBOARD.SERVICES.TITLE'})}
            </span>
          </div>
          <div className="row g-6">
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.CONNEXIONS.TITLE'})}
                icon="bi-diagram-3"
                iconColor="#3699ff"
                items={connexionsItems}
                isLoading={servicesLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.CONNEXIONS.MANAGE'})}
                manageTo="/services/connexion/liens"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(connexionsItems)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.TELEPHONIE.TITLE'})}
                icon="bi-telephone"
                iconColor="#7239ea"
                items={telephonieItems}
                isLoading={servicesLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.TELEPHONIE.MANAGE'})}
                manageTo="/services/telephonie/liste"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(telephonieItems)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.DOMAINES.TITLE'})}
                icon="bi-globe"
                iconColor="#50cd89"
                items={domainesItems}
                isLoading={domainesLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.DOMAINES.MANAGE'})}
                manageTo="/services/domaine/liste"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(domainesItems)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.EMAILS.TITLE'})}
                icon="bi-envelope"
                iconColor="#f1416c"
                items={emailsItems}
                isLoading={emailsLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.EMAILS.MANAGE'})}
                manageTo="/services/messagerie"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(emailsItems)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.WEB.TITLE'})}
                icon="bi-server"
                iconColor="#ffc700"
                items={webBddItems}
                isLoading={databasesLoading || comptesFtpLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.WEB.MANAGE'})}
                manageTo="/services/database"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(webBddItems)}
              />
            </div>
            <div className="col-12 col-lg-4">
              <ServiceMiniCard
                title={intl.formatMessage({id: 'DASHBOARD.SERVICES.INFRA.TITLE'})}
                icon="bi-hdd-stack"
                iconColor="#009ef7"
                items={infrastructureItems}
                isLoading={baiesLoading}
                manageLabel={intl.formatMessage({id: 'DASHBOARD.SERVICES.INFRA.MANAGE'})}
                manageTo="/services/baies"
                emptyMessage={serviceEmptyMsg}
                activeCountLabel={serviceActiveCount(infrastructureItems)}
              />
            </div>
          </div>
        </>
      )}

      {/* Ligne 4 : actualités Celeste */}
      <LatestNews
        fullWidth
        header={
          <div className="d-flex justify-content-between align-items-center mt-8 mb-6">
            <span className="fw-bold fs-2 text-gray-900">
              {intl.formatMessage({id: 'DASHBOARD.NEWS.TITLE'})}
            </span>
            <a
              href="https://www.celeste.fr/le-blog/"
              target="_blank"
              rel="noreferrer"
              className={`text-primary fw-semibold ${styles.softLink}`}
            >
              {intl.formatMessage({id: 'DASHBOARD.NEWS.SEE_ALL'})}
            </a>
          </div>
        }
      />

      <SearchModal
        show={searchOpen}
        onHide={() => setSearchOpen(false)}
        initialQuery={searchTerm}
        services={services ?? []}
        bills={bills ?? []}
        tickets={tickets ?? []}
        customer={customer}
        showServices={canSeeServices}
        showBills={canSeeBills}
        showTickets={canSeeTickets}
      />
    </>
  );
};

export default DashboardV2;
